import React, {useCallback, useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import moment from 'moment'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'
import TablePagination from '../../components/TablePagination/TablePagination'
import UposlenikDropdown from '../../components/UposlenikDropdown/UposlenikDropdown'
import {TableActionButton} from '../../components/TableActionBtn/TableActionButton'
import {PaginationModel} from '../../models/PaginationModel'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
    deleteOperacija,
    get,
    getOperationWorkers,
    kontrolaNaloga,
    operacije,
    radnici,
} from '../../api/plaviNalozi'
import {
    PlaviNalogOperationWorker,
    getPlaviNalogPrioritet,
    getPlaviNalogStatus,
    PlaviNalog,
    PlaviNalogOperacija,
    PlaviNalogRadnik,
    PlaviNalogRadniciParams,
} from '../../models/PlaviNalog'

type ActiveTab = 'detalji' | 'operacije' | 'radnici' | 'kontrola'

const formatDate = (date?: string) => (date ? moment(date).format('DD-MM-YYYY') : '-')
const formatDateTime = (date?: string) => (date ? moment(date).format('DD-MM-YYYY HH:mm') : '-')

const StatusBadge = ({status}: {status?: number}) => {
    const statusInfo = getPlaviNalogStatus(status)
    return <span className={`badge badge-light-${statusInfo.badge}`}>{statusInfo.label}</span>
}

const getRadnikName = (item: PlaviNalogRadnik) => {
    const radnikName = typeof item.radnik === 'string' ? item.radnik : item.radnik?.UposlenikImePrezime

    return radnikName ?? item.uposlenik?.UposlenikImePrezime ?? item.UposlenikImePrezime ?? '-'
}

const getRadnikOperacija = (item: PlaviNalogRadnik) =>
    (typeof item.operacija === 'object' ? item.operacija?.naziv : item.operacija) ??
    item.operacije?.naziv ??
    item.nazivOperacije ??
    '-'

const canDeleteOperation = (item: PlaviNalogOperacija) => Number(item.zavrsenaKolicina ?? NaN) === 0

const toNumber = (value?: number | string | boolean) => {
    if (value === undefined || value === null || value === '') {
        return undefined
    }

    const numberValue = Number(value)

    return Number.isFinite(numberValue) ? numberValue : undefined
}

const getBrojOperacijaZaOperaciju = (item: PlaviNalogOperacija) => {
    const brojOperacija = toNumber(item.operacija?.brojOperacija)

    return brojOperacija && brojOperacija > 0 ? brojOperacija : 1
}

const getRadnikOperationId = (item: PlaviNalogRadnik) => {
    const operacija = typeof item.operacija === 'object' ? item.operacija : undefined

    return toNumber(item.idOperacije) ?? toNumber(operacija?.id) ?? toNumber(item.operacije?.id)
}

const findOperacijaZaRadnika = (item: PlaviNalogRadnik, operacijeData: PlaviNalogOperacija[]) => {
    const operationId = getRadnikOperationId(item)

    if (operationId === undefined) {
        return undefined
    }

    return operacijeData.find((operacija) => {
        const plaviNalogOperacijaId = toNumber(operacija.idOperacije) ?? toNumber(operacija.operacija?.id)

        return plaviNalogOperacijaId === operationId || toNumber(operacija.id) === operationId
    })
}

const getTimestamp = (value?: number | string) => {
    const numberValue = toNumber(value)

    if (numberValue !== undefined) {
        return numberValue
    }

    if (!value || !moment(value).isValid()) {
        return undefined
    }

    return moment(value).unix()
}

const formatTime = (value?: number | string) => {
    const timestamp = getTimestamp(value)

    return timestamp !== undefined ? moment.unix(timestamp).format('HH:mm') : ''
}

const formatTimestampDate = (value?: number | string) => {
    const timestamp = getTimestamp(value)

    return timestamp !== undefined ? moment.unix(timestamp).format('DD-MM-YYYY') : ''
}

const calculateWorkedMinutesRaw = (item: PlaviNalogRadnik) => {
    const startTime = getTimestamp(item.startTime)
    const endTime = getTimestamp(item.endTime)

    if (startTime === undefined || endTime === undefined) {
        return undefined
    }

    return (endTime - startTime) / 60
}

const calculateWorkedMinutes = (item: PlaviNalogRadnik) => {
    const workedMinutes = calculateWorkedMinutesRaw(item)

    return workedMinutes !== undefined ? Math.round(workedMinutes) : undefined
}

const getOperationNorma = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const operacija = typeof item.operacija === 'object' ? item.operacija : undefined

    return (
        toNumber(operacijaNaloga?.norma) ??
        toNumber(operacijaNaloga?.operacija?.norma) ??
        toNumber(item.norma) ??
        toNumber(operacija?.norma) ??
        toNumber(item.operacije?.norma)
    )
}

const getOperationNormaMinuta = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const operacija = typeof item.operacija === 'object' ? item.operacija : undefined

    return (
        toNumber(operacijaNaloga?.normaMinuta) ??
        toNumber(operacijaNaloga?.operacija?.normaMinuta) ??
        toNumber(item.normaMinuta) ??
        toNumber(operacija?.normaMinuta) ??
        toNumber(item.operacije?.normaMinuta)
    )
}

const getBrojOperacija = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const operacija = typeof item.operacija === 'object' ? item.operacija : undefined
    const brojOperacija =
        toNumber(operacijaNaloga?.operacija?.brojOperacija) ??
        toNumber(operacijaNaloga?.brojOperacija) ??
        toNumber(item.brojOperacija) ??
        toNumber(operacija?.brojOperacija) ??
        toNumber(item.operacije?.brojOperacija)

    return brojOperacija && brojOperacija > 0 ? brojOperacija : 1
}

const calculateCompletedQuantity = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const kolicina = toNumber(item.kolicina)

    if (kolicina === undefined) {
        return undefined
    }

    return kolicina / getBrojOperacija(item, operacijaNaloga)
}

const getEnteredQuantity = (item: PlaviNalogRadnik) => toNumber(item.kolicina)

const formatQuantity = (value?: number) => {
    if (value === undefined) {
        return '-'
    }

    return Number.isInteger(value) ? value : value.toFixed(2)
}

const calculateNorma = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const workedMinutes = calculateWorkedMinutesRaw(item)
    const norma = getOperationNorma(item, operacijaNaloga)
    const normaMinuta = getOperationNormaMinuta(item, operacijaNaloga)

    if (workedMinutes === undefined || norma === undefined || !normaMinuta) {
        return undefined
    }

    return Math.round((workedMinutes * norma) / normaMinuta)
}

const calculateRealizacija = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const kolicina = getEnteredQuantity(item)
    const norma = calculateNorma(item, operacijaNaloga)

    if (kolicina === undefined || !norma) {
        return undefined
    }

    return Math.round((kolicina / norma) * 100)
}

const calculateRealizacijaOdstupanje = (item: PlaviNalogRadnik, operacijaNaloga?: PlaviNalogOperacija) => {
    const realizacija = calculateRealizacija(item, operacijaNaloga)

    return realizacija !== undefined ? `${realizacija - 100}%` : '-'
}

const extractPaginatorRows = (response: any) => {
    const paginator = response?.data?.data && Array.isArray(response.data.data) ? response.data : response
    const rows = paginator?.data ?? []

    return {
        rows: Array.isArray(rows) ? rows : [],
        currentPage: paginator?.current_page ?? 1,
        totalPage: paginator?.last_page ?? Math.max(1, Math.ceil((paginator?.total ?? rows.length) / (paginator?.per_page ?? 10))),
        perPage: paginator?.per_page ?? 10,
    }
}

type OperationWorkersCache = {
    [operationId: number]: {
        loading: boolean
        data?: PlaviNalogOperationWorker[]
    }
}

const PlaviNalogDetails: React.FC = () => {
    const {id} = useParams()
    const IdFromURL = Number(id)
    const [activeTab, setActiveTab] = useState<ActiveTab>('detalji')
    const [data, setData] = useState<Partial<PlaviNalog>>({})
    const [operacijeData, setOperacijeData] = useState<PlaviNalogOperacija[]>([])
    const [radniciData, setRadniciData] = useState<PlaviNalogRadnik[]>([])
    const [expandedOperations, setExpandedOperations] = useState<number[]>([])
    const [operationWorkersCache, setOperationWorkersCache] = useState<OperationWorkersCache>({})
    const [radniciPagination, setRadniciPagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })
    const [radniciParams, setRadniciParams] = useState<PlaviNalogRadniciParams>({
        page: 1,
        per_page: 10,
    })
    const [loading, setLoading] = useState(true)
    const [operacijeLoading, setOperacijeLoading] = useState(false)
    const [radniciLoading, setRadniciLoading] = useState(false)
    const [kontrolaKomentar, setKontrolaKomentar] = useState('')
    const [nalogUredan, setNalogUredan] = useState(false)
    const [kontrolaLoading, setKontrolaLoading] = useState(false)

    const loadDetails = useCallback(() => {
        if (!IdFromURL) {
            return
        }

        setLoading(true)
        get(IdFromURL)
            .then((response) => {
                setData(response)
                setKontrolaKomentar(response.nalogKontrolisaoKomentar ?? '')
                setNalogUredan(Boolean(response.nalogUredan))
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja.')
            )
            .finally(() => setLoading(false))
    }, [IdFromURL])

    const loadOperacije = useCallback(() => {
        if (!IdFromURL) {
            return
        }

        setOperacijeLoading(true)
        operacije(IdFromURL)
            .then((response) => setOperacijeData(Array.isArray(response) ? response : []))
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja operacija.')
            )
            .finally(() => setOperacijeLoading(false))
    }, [IdFromURL])

    const loadRadnici = useCallback(() => {
        if (!IdFromURL) {
            return
        }

        setRadniciLoading(true)
        radnici(IdFromURL, radniciParams)
            .then((response) => {
                const paginator = extractPaginatorRows(response)
                setRadniciData(paginator.rows)
                setRadniciPagination({
                    curPage: paginator.currentPage,
                    totalPage: paginator.totalPage,
                    perPage: paginator.perPage,
                })
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja radnika.')
            )
            .finally(() => setRadniciLoading(false))
    }, [IdFromURL, radniciParams])

    useEffect(() => {
        loadDetails()
    }, [loadDetails])

    useEffect(() => {
        if (activeTab === 'operacije') {
            loadOperacije()
        }
        if (activeTab === 'radnici') {
            if (operacijeData.length === 0) {
                loadOperacije()
            }
            loadRadnici()
        }
    }, [activeTab, loadOperacije, loadRadnici, operacijeData.length])

    const toggleOperationWorkers = (operationId: number) => {
        const isExpanded = expandedOperations.includes(operationId)

        setExpandedOperations((previous) =>
            isExpanded ? previous.filter((id) => id !== operationId) : [...previous, operationId]
        )

        if (isExpanded || operationWorkersCache[operationId]?.data || operationWorkersCache[operationId]?.loading) {
            return
        }

        setOperationWorkersCache((previous) => ({
            ...previous,
            [operationId]: {
                loading: true,
                data: [],
            },
        }))

        getOperationWorkers(IdFromURL, operationId)
            .then((response) => {
                setOperationWorkersCache((previous) => ({
                    ...previous,
                    [operationId]: {
                        loading: false,
                        data: Array.isArray(response) ? response : [],
                    },
                }))
            })
            .catch((error: any) => {
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja radnika operacije.')
                setOperationWorkersCache((previous) => ({
                    ...previous,
                    [operationId]: {
                        loading: false,
                        data: [],
                    },
                }))
            })
    }

    const onSaveKontrola = () => {
        if (!IdFromURL) {
            return
        }

        setKontrolaLoading(true)
        kontrolaNaloga(IdFromURL, kontrolaKomentar, nalogUredan)
            .then((response) => {
                notifyToast(true, response?.message ?? 'Kontrola naloga je uspješno sačuvana.')
                if (response?.data) {
                    setData(response.data)
                    setKontrolaKomentar(response.data.nalogKontrolisaoKomentar ?? '')
                    setNalogUredan(Boolean(response.data.nalogUredan))
                }
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
            .finally(() => setKontrolaLoading(false))
    }

    const onDeleteOperacija = (item: PlaviNalogOperacija) => {
        if (!IdFromURL) {
            return
        }

        if (!canDeleteOperation(item)) {
            notifyToast(false, 'Operacija se moÅ¾e obrisati samo ako je zavrÅ¡ena koliÄina 0.')
            return
        }

        const operationId = item.id

        deleteOperacija(IdFromURL, operationId)
            .then((response) => {
                notifyToast(true, response?.message ?? 'Operacija je obrisana.')
                setOperacijeData((previous) => previous.filter((item) => item.id !== operationId))
                setExpandedOperations((previous) => previous.filter((id) => id !== operationId))
                setOperationWorkersCache((previous) => {
                    const next = {...previous}
                    delete next[operationId]

                    return next
                })
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
    }

    return (
        <KTCard>
            <ToastContainer />
            <KTCardBody className='py-4'>
                <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-6 fw-bold mb-8'>
                    <li className='nav-item'>
                        <button
                            type='button'
                            className={`nav-link ${activeTab === 'detalji' ? 'active' : ''}`}
                            onClick={() => setActiveTab('detalji')}
                        >
                            Detalji
                        </button>
                    </li>
                    <li className='nav-item'>
                        <button
                            type='button'
                            className={`nav-link ${activeTab === 'operacije' ? 'active' : ''}`}
                            onClick={() => setActiveTab('operacije')}
                        >
                            Operacije
                        </button>
                    </li>
                    <li className='nav-item'>
                        <button
                            type='button'
                            className={`nav-link ${activeTab === 'radnici' ? 'active' : ''}`}
                            onClick={() => setActiveTab('radnici')}
                        >
                            Radnici
                        </button>
                    </li>
                    <li className='nav-item'>
                        <button
                            type='button'
                            className={`nav-link ${activeTab === 'kontrola' ? 'active' : ''}`}
                            onClick={() => setActiveTab('kontrola')}
                        >
                            Kontrola naloga
                        </button>
                    </li>
                </ul>

                {activeTab === 'detalji' && (
                    loading ? (
                        <LoadingComponent />
                    ) : (
                        <div className='row g-6'>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Broj naloga</div>
                                <div className='fs-5'>{data.brojNaloga ?? '-'}</div>
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Artikal</div>
                                <div className='fs-5'>{data.artikal?.ArtNaziv ?? data.artikal?.Naziv ?? '-'}</div>
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Količina</div>
                                <div className='fs-5'>{data.kolicina ?? '-'}</div>
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Planirani početak</div>
                                <div className='fs-5'>{formatDate(data.planiraniPocetak)}</div>
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Planirani kraj</div>
                                <div className='fs-5'>{formatDate(data.planiraniKraj)}</div>
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Status</div>
                                <StatusBadge status={data.status} />
                            </div>
                            <div className='col-md-4'>
                                <div className='fw-bold text-muted'>Prioritet</div>
                                <div className='fs-5'>{getPlaviNalogPrioritet(data.prioritet).label}</div>
                            </div>
                            <div className='col-12'>
                                <div className='fw-bold text-muted'>Napomena</div>
                                <div className='fs-5'>{data.napomena ?? '-'}</div>
                            </div>
                        </div>
                    )
                )}

                {activeTab === 'operacije' && (
                    operacijeLoading ? (
                        <LoadingComponent />
                    ) : (
                        <div className='table-responsive'>
                            <table className='table align-middle table-bordered gy-5 no-footer'>
                                <thead>
                                    <tr className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                                        <th className='text-white'>Id operacije</th>
                                        <th className='text-white'>Operacija</th>
                                        <th className='text-white'>Norma</th>
                                        <th className='text-white'>Norma minuta</th>
                                        <th className='text-white'>Broj operacija</th>
                                        <th className='text-white'>Planirana količina</th>
                                        <th className='text-white'>Zavrsena količina</th>
                                        <th className='text-white'>Planirano vrijeme</th>
                                        <th className='text-white'>Stvarno vrijeme</th>
                                        <th className='text-white'>Status</th>
                                        <th className='text-white'>Radnici</th>
                                        <th className='text-white'>Akcija</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {operacijeData.length > 0 ? (
                                        operacijeData.map((item) => {
                                            const operationId = item.id
                                            const workersState = operationWorkersCache[operationId]
                                            const workers = workersState?.data ?? []
                                            const isExpanded = expandedOperations.includes(operationId)
                                            const canDelete = canDeleteOperation(item)

                                            return (
                                                <React.Fragment key={item.id}>
                                                    <tr>
                                                        <td>{item.idOperacije}</td>
                                                        <td>{item.nazivOperacije ?? item.operacija?.naziv ?? item.naziv ?? '-'}</td>
                                                        <td>{item.norma ?? '-'}</td>
                                                        <td>{item.normaMinuta ?? '-'}</td>
                                                        <td>{getBrojOperacijaZaOperaciju(item)}</td>
                                                        <td>{item.planiranaKolicina ?? '-'}</td>
                                                        <td>{item.zavrsenaKolicina ?? '-'}</td>
                                                        <td>{item.planiranoVrijemeMinuta ?? item.planiranoVrijeme ?? '-'}</td>
                                                        <td>{item.stvarnoVrijemeMinuta ?? item.stvarnoVrijeme ?? '-'}</td>
                                                        <td><StatusBadge status={item.status} /></td>
                                                        <td>
                                                            <button
                                                                type='button'
                                                                className='btn btn-sm btn-light-primary'
                                                                onClick={() => toggleOperationWorkers(operationId)}
                                                            >
                                                                <KTIcon iconName={isExpanded ? 'up' : 'down'} className='fs-3' />
                                                                {isExpanded ? 'Sakrij' : 'Prikaži'}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {canDelete ? (
                                                                <TableActionButton
                                                                    showEdit={false}
                                                                    onDelete={() => onDeleteOperacija(item)}
                                                                />
                                                            ) : (
                                                                <span className='text-muted fw-semibold'>-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={12} className='bg-light'>
                                                                {workersState?.loading ? (
                                                                    <LoadingComponent />
                                                                ) : (
                                                                    <div className='p-4'>
                                                                        <div className='fw-bold mb-4'>Radnici na operaciji</div>
                                                                        <div className='table-responsive'>
                                                                            <table className='table table-row-bordered table-row-gray-300 gy-4 mb-0'>
                                                                                <thead>
                                                                                    <tr className='fw-bold text-muted'>
                                                                                        <th>Radnik</th>
                                                                                        <th>Ukupna količina</th>
                                                                                        <th>Ukupno vrijeme</th>
                                                                                        <th>Učinkovitost %</th>
                                                                                        <th>Broj prijava</th>
                                                                                        <th>Kvarovi</th>
                                                                                        <th>Zadnja aktivnost</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {workers.length > 0 ? (
                                                                                        workers.map((worker) => (
                                                                                            <tr key={worker.idUposlenik}>
                                                                                                <td>{worker.uposlenik?.UposlenikImePrezime ?? '-'}</td>
                                                                                                <td>{worker.ukupnoKomada ?? '-'}</td>
                                                                                                <td>{worker.ukupnoVrijeme ?? '-'}</td>
                                                                                                <td>
                                                                                                    {worker.prosjecnaUcinkovitost !== undefined
                                                                                                        ? `${worker.prosjecnaUcinkovitost}%`
                                                                                                        : '-'}
                                                                                                </td>
                                                                                                <td>{worker.brojPrijava ?? '-'}</td>
                                                                                                <td>{worker.brojKvarova ?? '-'}</td>
                                                                                                <td>{formatDateTime(worker.zadnjaAktivnost)}</td>
                                                                                            </tr>
                                                                                        ))
                                                                                    ) : (
                                                                                        <tr>
                                                                                            <td colSpan={7} className='text-center text-muted py-6'>
                                                                                                Nema radnika za ovu operaciju.
                                                                                            </td>
                                                                                        </tr>
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={12} className='text-center text-muted py-10'>
                                                Nema operacija.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {activeTab === 'radnici' && (
                    radniciLoading ? (
                        <LoadingComponent />
                    ) : (
                        <div>
                            <div className='row g-5 mb-8'>
                                <div className='col-lg-4'>
                                    <label className='form-label fw-bold'>Operacija</label>
                                    <select
                                        className='form-select form-select-solid'
                                        value={radniciParams.idOperacije ?? ''}
                                        onChange={(event) =>
                                            setRadniciParams({...radniciParams, idOperacije: event.target.value, page: 1})
                                        }
                                    >
                                        <option value=''>Sve operacije</option>
                                        {operacijeData.map((item) => (
                                            <option key={item.idOperacije ?? item.operacija?.id ?? item.id} value={item.idOperacije ?? item.operacija?.id ?? item.id}>
                                                {item.nazivOperacije ?? item.operacija?.naziv ?? item.naziv ?? '-'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='col-lg-4'>
                                    <label className='form-label fw-bold'>Radnik</label>
                                    <UposlenikDropdown
                                        value={radniciParams.idUposlenik}
                                        selectName='idUposlenik'
                                        onChange={(name, value) =>
                                            setRadniciParams({...radniciParams, idUposlenik: value, page: 1})
                                        }
                                    />
                                </div>
                                <div className='col-lg-3'>
                                    <label className='form-label fw-bold'>Kvar</label>
                                    <select
                                        className='form-select form-select-solid'
                                        value={radniciParams.kvar as string ?? ''}
                                        onChange={(event) => setRadniciParams({...radniciParams, kvar: event.target.value, page: 1})}
                                    >
                                        <option value=''>Svi</option>
                                        <option value='1'>Da</option>
                                        <option value='0'>Ne</option>
                                    </select>
                                </div>
                            </div>
                            <div className='table-responsive'>
                                <table className='table align-middle table-bordered gy-5 no-footer'>
                                    <thead>
                                        <tr className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                                            <th className='text-white'>Radnik</th>
                                            <th className='text-white'>Id zapisa</th>
                                            <th className='text-white'>Operacija</th>
                                            <th className='text-white'>Datum</th>
                                            <th className='text-white'>Početak</th>
                                            <th className='text-white'>Kraj</th>
                                            <th className='text-white'>Radio min</th>
                                            <th className='text-white'>Broj operacija</th>
                                            <th className='text-white'>Količina</th>
                                            <th className='text-white'>Završeno komada</th>
                                            <th className='text-white'>Norma</th>
                                            <th className='text-white'>Realizacija</th>
                                            <th className='text-white'>Realizacija %</th>
                                            <th className='text-white'>Kvar</th>
                                            <th className='text-white'>Id prijave</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {radniciData.length > 0 ? (
                                            radniciData.map((item, index) => (
                                                <tr key={item.id ?? index}>
                                                    <td>{getRadnikName(item)}</td>
                                                    <td>{item.id ?? '-'}</td>
                                                    <td>{getRadnikOperacija(item)}</td>
                                                    <td>{formatTimestampDate(item.startTime)}</td>
                                                    <td>{formatTime(item.startTime)}</td>
                                                    <td>{formatTime(item.endTime)}</td>
                                                    <td>{calculateWorkedMinutes(item) ?? ''}</td>
                                                    <td>{getBrojOperacija(item, findOperacijaZaRadnika(item, operacijeData))}</td>
                                                    <td>{formatQuantity(getEnteredQuantity(item))}</td>
                                                    <td>{formatQuantity(calculateCompletedQuantity(item, findOperacijaZaRadnika(item, operacijeData)))}</td>
                                                    <td>{calculateNorma(item, findOperacijaZaRadnika(item, operacijeData)) ?? item.norma ?? '-'}</td>
                                                    <td>{calculateRealizacija(item, findOperacijaZaRadnika(item, operacijeData)) ?? item.ucinkovitost ?? item.učinkovitost ?? item.realizacija ?? '-'}</td>
                                                    <td>{calculateRealizacijaOdstupanje(item, findOperacijaZaRadnika(item, operacijeData))}</td>
                                                    <td>{item.kvar ? 'Da' : 'Ne'}</td>
                                                    <td>{item.prijavaKvaradId ?? item.prijavaKvaraId ?? '-'}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={15} className='text-center text-muted py-10'>
                                                    Nema podataka.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination
                                updatePage={(page, per_page) =>
                                    setRadniciParams({...radniciParams, page: page, per_page: per_page})
                                }
                                pagination={radniciPagination}
                                isLoading={radniciLoading}
                            />
                        </div>
                    )
                )}

                {activeTab === 'kontrola' && (
                    <div className='card border'>
                        <div className='card-body p-6'>
                            <label className='form-label fw-bold'>Komentar kontrole naloga</label>
                            <textarea
                                className='form-control form-control-solid mb-6'
                                rows={6}
                                maxLength={1000}
                                value={kontrolaKomentar}
                                onChange={(event) => setKontrolaKomentar(event.target.value)}
                                placeholder='Unesite komentar za kontrolu naloga...'
                            />
                            <div className='form-check form-check-custom form-check-solid mb-6'>
                                <input
                                    id='plavi-nalog-uredan'
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={nalogUredan}
                                    onChange={(event) => setNalogUredan(event.target.checked)}
                                />
                                <label className='form-check-label fw-bold' htmlFor='plavi-nalog-uredan'>
                                    Nalog je uredan i može se napraviti ulaz
                                </label>
                            </div>
                            <div className='d-flex justify-content-between align-items-center'>
                                <span className='text-muted'>{kontrolaKomentar.length}/1000</span>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    disabled={kontrolaLoading}
                                    onClick={onSaveKontrola}
                                >
                                    {kontrolaLoading ? 'Spremanje...' : 'Sačuvaj'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </KTCardBody>
        </KTCard>
    )
}

export default PlaviNalogDetails
